<template>
  <div>
    <div class="c-section grey">
      <div class="c-section white">
        <div class=cta v-if="loading">
          <div class="spinner">
            <img src="@assets/images/_icon-spinner.svg" alt="Loading Spinner">
          </div>
        </div>
        <matrix v-else
        :fetching-message="'No submissions.'"
        class="clear-table associated-form-submissions"
        :has-footer="true"
        :has-data="visibleSubmissions.length > 0">
        <template slot="table-header" v-if="visibleSubmissions.length">
          <t-prefix is-heading/>
          <t-column is-heading>
            <sort-column v-if="hasSort" :value="FormListSortColumn.FormName" v-bind="currentSortDetails" @click="reSortSubmissions">
              Form Name
            </sort-column>
            <template v-else>
              Form Name
            </template>
          </t-column>
          <t-column is-heading>
            <sort-column v-if="hasSort" :value="FormListSortColumn.SubmissionDate" v-bind="currentSortDetails" @click="reSortSubmissions">
              Submitter and Submission Date
            </sort-column>
            <template v-else>
              Submitter and Submission Date
            </template>
          </t-column>
          <t-column is-heading>
            <sort-column v-if="hasSort" :value="FormListSortColumn.StatusChangeDate" v-bind="currentSortDetails" @click="reSortSubmissions">
              Status
            </sort-column>
            <template v-else>
              Status
            </template>
          </t-column>
        </template>
        <template slot="table-body">
          <template v-for="submission in visibleSubmissions">
            <t-row class="form form-submission-listing" :key="submission.id">
              <t-prefix>
                <a :href="`/submissions/${submission.id}`" :class="{'disabled-link': isPreview || isInDetailViewEditMode}">
                  <icon class="form"></icon>
                </a>
              </t-prefix>
              <t-column class="row-title">
                <a :href="`/submissions/${submission.id}`" :class="{'disabled-link': isPreview || isInDetailViewEditMode}">
                  <span>{{ submission.title }}</span>
                </a>
              </t-column>
              <t-column>
                <template v-if="submission.submitted_at">
                  <b>{{submission.submitter_name}}</b> on
                  <br>
                  {{ toDatetime(submission.submitted_at) }}
                </template>
                <template v-else>
                  Not yet submitted.
                </template>
              </t-column>
              <t-column table-element>
                <crayon :color="[submission.statusClass]">{{submission.current_application_status_name}}</crayon> {{submission.statusTimePrefix}}
                <br>
                {{ statusUpdatedAt(submission) }}
              </t-column>
            </t-row>
          </template>
        </template>
      </matrix>
      </div>
      <div class="container align-right" v-if="visibleSubmissions.length > 0">{{countDisplay}}</div>
    </div>
    <template v-if="shouldShowMore && !loading">
      <div class="cta">
        <slot name="cta">
          <button class="btn-link"
                  type="button"
                  @click="showMore">Show More</button>
        </slot>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import FormSubmission from "~/models/formSubmission/formSubmission";
  import Datetimeable from '~/mixins/datetimeable';
  import SortColumn from '~/components/sortColumn.vue';
  import {IPagination} from "~/models/pagination";
  import {FormListSortColumn} from "../../../formability/detailViewBuilder/enums/formListSortColumn";
  import ColumnSortMixin from "~/mixins/columnSortMixin";
  import {Hash} from "~/interfaces/hashInterfaces";
  import {Area} from "../../../formability/modelViews/enums/area";

  export default Vue.extend({
    components: {
      SortColumn
    },
    mixins: [
      Datetimeable,
      ColumnSortMixin
    ],
    data() {
      return {
        visibleCount: null,
        FormListSortColumn: FormListSortColumn,
        currentSortDetails: null
      }
    },
    props: {
      defaultSortDetails: {
        type: Object as () => {direction: IPagination["direction"], sort: IPagination['sort']},
        default: null
      },
      isInDetailViewEditMode:{
        type: Boolean,
        default: false
      },
      showLinkToAll: {
        type: Object as () => {name: Area, params: Hash},
        default: null
      },
      submissions: {
        type: Array as () => Array<FormSubmission>
      },
      initialCount: {
        type: Number,
        default: 5
      },
      totalCount: {
        type: Number,
        default: null
      },
      addMoreCount: {
        type: Number,
        default: 5
      },
      loading: {
        type: Boolean,
        default: false
      },
      filtersOn: {
        type: Boolean,
        default: false
      },
      submissionMetadata: {
        type: Object as () => {querySubmissionCount: number, totalSubmissionCount: number},
        default: null
      }
    },
    mounted(): void{
      this.visibleCount = this.initialCount;
      if (!!this.defaultSortDetails) {
        this.currentSortDetails = { direction: null, sort: null }
        this.currentSortDirection = this.defaultSortDetails.direction;
        this.currentSortByColumn = this.defaultSortDetails.sort;
      }
    },
    computed: {
      countDisplay() {
        const pluralize = (word, count) => {
          if (!count || count > 1) {
            return `${word}s`
          } else return word;
        }
        if (this.submissionMetadata) {
          let totalCount;
          if (this.filtersOn) {
            totalCount = this.submissionMetadata.querySubmissionCount;
            return `${this.visibleSubmissions.length} / ${totalCount} ${pluralize("Submission", totalCount)}`
          } else {
            totalCount = this.submissionMetadata.totalSubmissionCount;
            return `${this.visibleSubmissions.length} / ${totalCount} ${pluralize("Submission", totalCount)}`
          }
        } else return ""
      },
      hasSort(): boolean {
        return !!this.currentSortDetails
      },
      visibleSubmissions(): Array<FormSubmission>{
        return this.submissions.slice(0, this.visibleCount);
      },
      shouldShowMore(): boolean{
        if (!!this.totalCount) {
          return this.visibleSubmissions.length < this.totalCount
        } else {
          return this.visibleSubmissions.length < this.submissions.length
        }
      },
      currentSortDirection: {
        get() {
          return this.currentSortDetails.direction;
        },
        set(val) {
          this.currentSortDetails.direction = val;
        }
      },
      currentSortByColumn: {
        get() {
          return this.currentSortDetails.sort;
        },
        set(val: FormListSortColumn) {
          return this.currentSortDetails.sort = val;
        }
      },
      pagination(): Partial<IPagination> {
        return {
          sort: this.currentSortByColumn,
          direction: this.currentSortDirection
        }
      }
    },
    methods: {
      statusUpdatedAt(submission): string {
        return this.toDatetime(submission.statusTime)
      },
      setNewSort() {
        this.currentSortDirection = this.reverseSort(this.currentSortDirection)
      },
      reSortSubmissions(column) {
        if (column === this.currentSortByColumn) {
          this.setNewSort()
        } else {
          this.currentSortByColumn = column;
          this.currentSortDirection = this.defaultSortDetails.direction;
        }
        this.$emit('updatePagination', this.pagination)
      },
      showMore(): void{
        this.visibleCount += this.addMoreCount
      },
      toDatetime(time): string{
        return this.$options.filters.datetime(time);
      },
    }
  })
</script>